import React, { useEffect, useState, useRef } from 'react';

// Planet data matching Landing page
const agents = [
    { name: 'Sun', symbol: '☉', color: '#FFE5A0' },
    { name: 'Moon', symbol: '☽', color: '#E8E4F0' },
    { name: 'Mercury', symbol: '☿', color: '#B8E6F5' },
    { name: 'Venus', symbol: '♀', color: '#FFD4E5' },
    { name: 'Mars', symbol: '♂', color: '#FF9A8B' },
    { name: 'Jupiter', symbol: '♃', color: '#C9B5E8' },
    { name: 'Saturn', symbol: '♄', color: '#9B7BC8' },
    { name: 'Uranus', symbol: '⛢', color: '#7FE8E8' },
    { name: 'Neptune', symbol: '♆', color: '#9BB5FF' }
];

interface PlanetSpeakerProps {
    activeAgent: string | null;
    isSpeaking: boolean;
}

const PlanetSpeaker: React.FC<PlanetSpeakerProps> = ({ activeAgent, isSpeaking }) => {
    const [rotationAngle, setRotationAngle] = useState(0);
    const [windowWidth, setWindowWidth] = useState(typeof window !== 'undefined' ? window.innerWidth : 1024);
    const containerRef = useRef<HTMLDivElement>(null);
    const activePlanetRef = useRef<HTMLDivElement>(null);

    // Handle window resize
    useEffect(() => {
        const handleResize = () => setWindowWidth(window.innerWidth);
        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, []);

    // Auto-rotate ring slowly when idle
    useEffect(() => {
        if (isSpeaking) return;
        const interval = setInterval(() => {
            setRotationAngle(prev => prev + 0.1); // Slower rotation for mystical feel
        }, 50);
        return () => clearInterval(interval);
    }, [isSpeaking]);

    // Scroll active planet into center view
    useEffect(() => {
        if (activeAgent && activePlanetRef.current && containerRef.current) {
            activePlanetRef.current.scrollIntoView({
                behavior: 'smooth',
                block: 'nearest',
                inline: 'center'
            });
        }
    }, [activeAgent]);

    // Find active agent index
    const activeIndex = agents.findIndex(a => a.name === activeAgent);
    const activePlanet = activeIndex >= 0 ? agents[activeIndex] : null;

    return (
        <div className="w-full flex justify-center py-6 relative z-30">
            {/* Horizontal Planet Line - Fixed overflow */}
            <div
                ref={containerRef}
                className="flex items-center gap-3 sm:gap-6 px-6 py-3 rounded-full bg-white/5 backdrop-blur-md border border-white/10 shadow-2xl overflow-x-auto overflow-y-hidden max-w-full no-scrollbar"
                style={{
                    scrollbarWidth: 'none',
                    msOverflowStyle: 'none',
                    WebkitOverflowScrolling: 'touch'
                }}
            >
                {agents.map((agent) => {
                    const isActive = agent.name === activeAgent;
                    const isDimmed = isSpeaking && !isActive;

                    return (
                        <div
                            key={agent.name}
                            ref={isActive ? activePlanetRef : null}
                            className={`relative flex flex-col items-center justify-center transition-all duration-500 ease-out flex-shrink-0
                                ${isActive ? 'scale-125 mx-2' : 'scale-100'}
                                ${isDimmed ? 'opacity-30 grayscale-[0.5] scale-90' : 'opacity-100'}
                            `}
                        >
                            {/* Planet Orb */}
                            <div
                                className={`w-10 h-10 sm:w-12 sm:h-12 rounded-full flex items-center justify-center relative transition-all duration-500
                                    ${isActive ? 'shadow-[0_0_30px_rgba(255,255,255,0.3)]' : 'hover:bg-white/10'}
                                `}
                                style={{
                                    backgroundColor: isActive ? `${agent.color}20` : 'transparent',
                                    border: `1px solid ${isActive ? agent.color : `${agent.color}40`}`,
                                    boxShadow: isActive ? `0 0 20px ${agent.color}40, inset 0 0 10px ${agent.color}20` : 'none'
                                }}
                            >
                                <span
                                    className={`text-lg sm:text-xl transition-all duration-300 ${isActive ? 'text-white scale-110' : ''}`}
                                    style={{ color: isActive ? agent.color : `${agent.color}90` }}
                                >
                                    {agent.symbol}
                                </span>

                                {/* Active Pulse Ring */}
                                {isActive && (
                                    <div
                                        className="absolute inset-0 rounded-full animate-ping-slow opacity-50"
                                        style={{ border: `1px solid ${agent.color}` }}
                                    />
                                )}
                            </div>

                            {/* Label (Only visible when active or hovered) */}
                            <div className={`absolute -bottom-6 whitespace-nowrap transition-all duration-300 ${isActive ? 'opacity-100 translate-y-0' : 'opacity-0 -translate-y-2'}`}>
                                <span
                                    className="text-[10px] uppercase tracking-widest font-heading"
                                    style={{ color: agent.color }}
                                >
                                    {agent.name}
                                </span>
                            </div>
                        </div>
                    );
                })}
            </div>
        </div>
    );
};

export default PlanetSpeaker;
